import api from './api';

// Types
export interface List {
  id?: number;
  uuid: string;
  name: string;
  description?: string;
  user_id?: number;
  created_at: string;
  updated_at: string;
  poi_count: number;
}

export interface ListPOI {
  uuid: string;
  name: string;
  latitude: string;
  longitude: string;
  city?: string;
  country?: string;
  category?: string;
  categories?: string;
  description?: string;
  position: number;
  added_at: string;
}

export interface ListDetail {
  list: List;
  pois: ListPOI[];
}

export interface CreateListRequest {
  name: string;
  description?: string;
  poi_uuids?: string[];
}

export interface UpdateListRequest {
  name?: string;
  description?: string;
}

export interface AddPOIsRequest {
  poi_uuids: string[];
}

export interface RemovePOIsRequest {
  poi_uuids: string[];
}

// List Service Functions
export const listService = {
  // Get all lists for the authenticated user
  async getAllLists(): Promise<{ lists: List[] }> {
    try {
      const response = await api.get('/lists');
      return response.data;
    } catch (error) {
      console.error('Error fetching lists:', error);
      throw new Error('Failed to fetch lists');
    }
  },

  // Get a specific list by UUID with all POIs
  async getListByUuid(uuid: string): Promise<ListDetail> {
    try {
      const response = await api.get(`/lists/${uuid}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching list:', error);
      throw new Error('Failed to fetch list');
    }
  },

  // Create a new list
  async createList(data: CreateListRequest): Promise<{ list: List }> {
    try {
      const response = await api.post('/lists', data);
      return response.data;
    } catch (error) {
      console.error('Error creating list:', error);
      throw new Error('Failed to create list');
    }
  },

  // Update a list's name or description
  async updateList(uuid: string, data: UpdateListRequest): Promise<{ list: List }> {
    try {
      const response = await api.put(`/lists/${uuid}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating list:', error);
      throw new Error('Failed to update list');
    }
  },

  // Delete a list
  async deleteList(uuid: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/lists/${uuid}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting list:', error);
      throw new Error('Failed to delete list');
    }
  },

  // Add POIs to a list
  async addPOIsToList(uuid: string, data: AddPOIsRequest): Promise<{ message: string }> {
    try {
      const response = await api.post(`/lists/${uuid}/pois`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding POIs to list:', error);
      throw new Error('Failed to add POIs to list');
    }
  },

  // Remove POIs from a list
  async removePOIsFromList(uuid: string, data: RemovePOIsRequest): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/lists/${uuid}/pois`, { data });
      return response.data;
    } catch (error) {
      console.error('Error removing POIs from list:', error);
      throw new Error('Failed to remove POIs from list');
    }
  },

  // Reorder POIs in a list
  async reorderPOIs(uuid: string, data: { poi_uuids: string[] }): Promise<{ message: string }> {
    try {
      const response = await api.put(`/lists/${uuid}/pois/reorder`, data);
      return response.data;
    } catch (error) {
      console.error('Error reordering POIs in list:', error);
      throw new Error('Failed to save order');
    }
  },
};

export default listService;
